// @systeme: interface
// Dans Moi : les rappels sur ce téléphone. Un interrupteur, un essai, et l'explication
// qui compte sur iPhone (l'app doit être installée sur l'écran d'accueil).
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import type { EtatNotifications } from '@foyer/shared';
import { api } from '../lib/api';
import {
  abonnementActuel,
  abonnerCetAppareil,
  desabonnerCetAppareil,
  estInstallee,
  etatPermission,
} from '../lib/notifications';
import { useMessages } from './Messages';

export function SectionNotifications() {
  const { dire, direErreur } = useMessages();
  const [etat, setEtat] = useState<EtatNotifications | undefined>();
  const [cetAppareil, setCetAppareil] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const permission = etatPermission();

  useEffect(() => {
    api.notifications.etat().then(setEtat).catch(direErreur);
    abonnementActuel()
      .then((a) => setCetAppareil(a !== null))
      .catch(() => setCetAppareil(false));
  }, [direErreur]);

  async function basculerCetAppareil(activer: boolean) {
    if (!etat?.clePublique) return;
    setEnCours(true);
    try {
      if (activer) {
        const ok = await abonnerCetAppareil(etat.clePublique);
        setCetAppareil(ok);
        if (ok) dire('C’est noté : les rappels arriveront ici.');
        else dire('Le téléphone n’a pas donné son accord. Tu peux réessayer dans ses réglages.');
      } else {
        await desabonnerCetAppareil();
        setCetAppareil(false);
      }
      setEtat(await api.notifications.etat());
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  async function basculerPreference(pushActif: boolean) {
    try {
      setEtat(await api.notifications.preferences({ pushActif }));
    } catch (e) {
      direErreur(e);
    }
  }

  async function essai() {
    try {
      await api.notifications.essai();
      dire('Envoyé. Elle devrait arriver dans quelques secondes.');
    } catch (e) {
      direErreur(e);
    }
  }

  if (!etat) return null;

  return (
    <Box>
      <Typography variant="h2" component="h2" gutterBottom>
        Mes rappels
      </Typography>
      {etat.clePublique === null ? (
        <Typography color="text.secondary">
          Les rappels sur le téléphone ne sont pas encore activés sur ce serveur.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={etat.preferences.pushActif}
                onChange={(e) => void basculerPreference(e.target.checked)}
              />
            }
            label="Recevoir des rappels"
          />
          {permission === 'indisponible' ? (
            <Typography color="text.secondary">
              {estInstallee()
                ? 'Ce navigateur ne sait pas envoyer de rappels.'
                : 'Sur iPhone, installe d’abord l’app sur l’écran d’accueil (partage → « Sur l’écran d’accueil »), puis reviens ici.'}
            </Typography>
          ) : permission === 'refusee' ? (
            <Typography color="text.secondary">
              Ce téléphone a refusé les notifications. Tu peux les rouvrir dans ses réglages.
            </Typography>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={cetAppareil}
                  disabled={enCours || !etat.preferences.pushActif}
                  onChange={(e) => void basculerCetAppareil(e.target.checked)}
                />
              }
              label="Sur ce téléphone"
            />
          )}
          <Typography variant="body2" color="text.secondary">
            {etat.appareils === 0
              ? 'Aucun appareil abonné pour le moment.'
              : etat.appareils === 1
                ? 'Un appareil abonné.'
                : `${etat.appareils} appareils abonnés.`}{' '}
            Jamais de rappel entre 22h et 7h.
          </Typography>
          {cetAppareil && (
            <Button
              variant="outlined"
              onClick={() => void essai()}
              sx={{ alignSelf: 'flex-start' }}
            >
              M’envoyer un essai
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
